import { useMemo, type FC, useEffect, type KeyboardEvent, useCallback, useRef } from 'react'
import { observer } from 'mobx-react-lite'
import { useSearchParams } from 'react-router-dom'
import { uiStore } from 'shared/store/uiStore'
import { layoutStore } from 'shared/layout'
import { Button, ClickAwayListener, Dropdown } from 'shared/ui'
import { makeTextFieldAutoFocus, TextField } from 'shared/ui/TextField'
import { SearchDropdown } from 'widgets/SearchDropdown/ui'
import { DropdownAllSearch } from 'widgets/SearchDropdown/ui/DropdownAllSearch/DropdownAllSearch'
import {
  ConversationSearchContext,
  useConversationSearchContext,
} from 'pages/conversations/ui/ConversationHeaderSearch/context/conversationSearchContext'
import { type ConversationSearchStore } from 'pages/conversations/ui/ConversationHeaderSearch/store/conversationSearchStore'
import { MobileSearch } from 'pages/conversations/ui/ConversationHeaderSearch/ui/MobileSearch/MobileSearch'
import styles from 'pages/conversations/ui/ConversationHeaderSearch/ui/styles.module.scss'

type SearchParamsPrevValues = {
  contacts: number[]
  teams: number[]
}

type SearchParamsArrayKey = 'contacts' | 'teams'

type SearchParamsCheck = {
  current: number[]
  prev: number[]
  handler?: (value: number[]) => void
  key: SearchParamsArrayKey
}

const ConversationHeaderSearchContent = observer(() => {
  const {
    reset,
    disposeReactions,
    hasSelectedSearch,
    handleSelectedSearch,
    searchDropdownStore,
    handleSearch,
    searchParams: searchParamsInfo,
  } = useConversationSearchContext()

  const [searchParams, setSearchParams] = useSearchParams()

  const prevValuesRef = useRef<SearchParamsPrevValues>({
    contacts: [],
    teams: [],
  })

  const { actionSearch, search } = searchDropdownStore
  const { startFocusingMode, endFocusingMode } = uiStore
  const wrapperId = 'react__conversation-header-search__wrapper'

  const onKeydownSearch = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      event.preventDefault()
      handleSelectedSearch(search)
    }
  }

  const removeSearchParams = useCallback(() => {
    if (searchParams.has('query')) {
      searchParams.delete('query')
      setSearchParams(searchParams)
    }
  }, [searchParams, setSearchParams])

  const handleReset = useCallback(() => {
    reset()
    removeSearchParams()
  }, [reset, removeSearchParams])

  useEffect(() => {
    return () => {
      uiStore.endFocusingMode()
      disposeReactions()
    }
  }, [])

  useEffect(() => {
    const prev = prevValuesRef.current
    const arraysEqual = (a: number[], b: number[]) => {
      if (a.length !== b.length) return false
      const sortedA = [...a].sort((x, y) => x - y)
      const sortedB = [...b].sort((x, y) => x - y)
      return sortedA.every((val, idx) => val === sortedB[idx])
    }

    const checks: SearchParamsCheck[] = [
      {
        current: searchParamsInfo.contacts,
        prev: prev.contacts,
        key: 'contacts',
      },
      {
        current: searchParamsInfo.teams,
        prev: prev.teams,
        key: 'teams',
      },
    ]

    checks.forEach(({ current, prev: prevValue, key }) => {
      if (!arraysEqual(prevValue, current)) {
        prevValuesRef.current[key] = current
      }
    })
  }, [searchParamsInfo.contacts, searchParamsInfo.teams])

  if (layoutStore.isMobileView) return <MobileSearch />

  return (
    <ClickAwayListener onClickAway={endFocusingMode}>
      <div className={styles.wrap} id={wrapperId} onClick={startFocusingMode}>
        <div className={styles.searchWrapper}>
          <div className={styles.searchInputWrap}>
            <SearchDropdown store={searchDropdownStore} />
            <Dropdown
              ariaLabel={'ConversationHeaderSearch'}
              show={search.length > 2}
              triggerComponent={() => (
                <TextField
                  className={styles.textField}
                  variant='integrated'
                  InputProps={{
                    onKeyDown: onKeydownSearch,
                    placeholder: 'Search conversation',
                    onFocus: startFocusingMode,
                    onBlur: endFocusingMode,
                  }}
                  onChange={(value) => {
                    actionSearch({
                      value: value,
                    })
                    handleSearch(value)
                  }}
                  value={search}
                  mainActions={[makeTextFieldAutoFocus({ withFocus: !search })]}
                />
              )}
              placement={'bottom-start'}
              customComponent={(onCloseMenu) => {
                if (search.length < 3 || hasSelectedSearch) return <></>

                return <DropdownAllSearch store={searchDropdownStore} onCloseMenu={onCloseMenu} />
              }}
            />
          </div>

          <Button
            contained={'secondary'}
            tooltipProps={{
              label: 'Cancel',
              disableInteractive: true,
            }}
            typeBtn={'text'}
            text={'Cancel'}
            onClick={handleReset}
          />
        </div>
      </div>
    </ClickAwayListener>
  )
})

type IConversationHeaderSearchProps = {
  store: ConversationSearchStore
}

export const ConversationHeaderSearch: FC<IConversationHeaderSearchProps> = observer(
  ({ store }) => {
    return useMemo(
      () => (
        <ConversationSearchContext.Provider value={store}>
          <ConversationHeaderSearchContent />
        </ConversationSearchContext.Provider>
      ),
      []
    )
  }
)
